<template>
  <FormularioCadastro
    module-name="Perfil Tributário"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
    :ocultar-salvar="!podeEditar"
  >
    <template #acoes-header>
      <span class="badge bg-dark border border-secondary-subtle px-3 py-2 me-3 d-flex align-items-center" style="font-size: 0.85rem;">
        <i class="bi bi-shield-check me-2 text-info"></i> Nível Fiscal: {{ nivelFiscalDescricao }}
      </span>

      <button
        v-if="isEditing"
        type="button"
        class="btn-icon-minimalista me-3"
        title="Ver Histórico de Alterações"
        @click="mostrarAuditoria = true"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME: DADOS BÁSICOS                                          -->
    <!-- ============================================================ -->
    <div class="card mb-4 shadow-sm border-0">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-file-earmark-text me-2"></i>Dados Básicos</h6>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-top">
          <div class="col-md-7">
            <CampoInput
              id="descricao"
              label="Descrição do Perfil Tributário"
              v-model="item.descricao"
              placeholder="Ex: Venda Tributada Integralmente (ICMS 18%)"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoSelect
              id="lstNaturezaItemAtendido"
              label="Natureza do Item Atendido"
              v-model="item.lstNaturezaItemAtendido"
              :options="naturezaOptions"
              option-value="id"
              option-label="label"
              required
              :disabled="!podeEditar"
              dica="Define quais impostos serão exigidos neste perfil. Produto Físico exige ICMS/IPI/PIS/COFINS. Serviço exige ISS e Retenções."
            />
          </div>
          <div class="col-md-2">
            <CampoSelect
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              :options="listas.LstStatus || []"
              option-label="textoLista"
              :option-value="optionValueStatusField"
              required
              :disabled="!podeEditar"
            />
          </div>
        </div>
        <div class="row g-3 mt-1">
          <div class="col-md-3">
            <CampoInput
              id="dataInicioVigencia"
              label="Data de Início (Vigência)"
              type="date"
              v-model="item.dataInicioVigencia"
              placeholder="Opcional (Em branco = Sem vigência definida)"
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="dataFimVigencia"
              label="Data de Término (Vigência)"
              type="date"
              v-model="item.dataFimVigencia"
              placeholder="Opcional (Em branco = Indeterminado)"
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-3">
            <CampoInput
              id="fatorImpostos"
              label="Fator de Impostos (Gross-up)"
              v-model="item.fatorImpostos"
              type="decimal"
              :casas-decimais="4"
              placeholder="Ex: 0,7275"
              :disabled="!podeEditar || fatorAutomatico"
              dica="Cálculo por dentro: Preço de Venda = Custo / Fator. Ex: 1 - (ICMS + PIS + COFINS + CBS + IBS)/100."
            >
              <template #label-extra>
                <span
                  v-if="fatorAutomatico"
                  class="badge bg-info-subtle text-info-emphasis border border-info-subtle"
                  style="font-size: 0.68rem;"
                  :title="memoriaCalculoTooltip"
                >
                  <i class="bi bi-lock-fill me-1"></i>Calculado
                </span>
              </template>
            </CampoInput>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- BLOCO: PRODUTO FÍSICO                                         -->
    <!-- ============================================================ -->
    <template v-if="naturezaAtendido === PROD_NATUREZA.ProdutoFisico || !naturezaAtendido">
      <!-- Quadro: ICMS & Incentivos Regionais -->
      <div class="card mb-4 shadow-sm border-0">
        <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
          <h6 class="mb-0 text-body-emphasis"><i class="bi bi-calculator me-2"></i>Imposto Estadual (ICMS & Benefícios)</h6>
        </div>
        <div class="card-body">
          <!-- Linha 1: Base ICMS -->
          <div class="row g-3">
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstCstIcms"
                label="CST ICMS (Normal)"
                v-model="item.lstCstIcms"
                tabela="PerfilTributario"
                :required="isModoXerife"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstCsosn"
                label="CSOSN (Simples)"
                v-model="item.lstCsosn"
                tabela="PerfilTributario"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="icmsAliquota"
                label="Alíquota ICMS Cheia (%)"
                v-model="item.icmsAliquota"
                type="moeda"
                :casas-decimais="4"
                :required="tipoIncentivo !== FISC_TP_INCENTIVO.Isencaodeferimento"
                :disabled="!podeEditar"
              >
                <template #label-extra>
                  <span v-if="campoIcmsFator === 'icmsAliquota'" class="d-inline-flex align-items-center gap-1" @click.stop>
                    <input
                      id="chkFatorIcms"
                      class="form-check-input m-0"
                      type="checkbox"
                      v-model="fatorComponentes.icmsAliquota"
                      :disabled="!podeEditar"
                    >
                    <label class="text-body-secondary fst-italic mb-0" for="chkFatorIcms" style="font-size: 0.72rem; cursor: pointer;" title="O ICMS entra no fator usando a alíquota cheia.">Compõe Fator</label>
                  </span>
                </template>
              </CampoInput>
            </div>
            <div class="col-md-3">
              <CampoInput
                id="difalAliquota"
                label="DIFAL (%)"
                v-model="item.difalAliquota"
                type="moeda"
                :casas-decimais="4"
                placeholder="Ex: 5%"
                :disabled="!podeEditar"
                dica="Diferencial de Alíquota para operações interestaduais com consumidor final. Ex: 5% = 17% (ES) - 12% (SP→ES)"
              />
            </div>
          </div>

          <!-- Linha 1.5: Substituição Tributária (ICMS-ST) -->
          <div class="row g-3 mt-1">
            <div class="col-md-4">
              <CampoInput
                id="icmsStMvaPercentual"
                label="MVA ICMS-ST (%)"
                v-model="item.icmsStMvaPercentual"
                type="moeda"
                :casas-decimais="4"
                placeholder="Ex: 50%"
                :disabled="!podeEditar"
                dica="Margem de Valor Agregado para cálculo da base do ICMS-ST."
              />
            </div>
            <div class="col-md-4">
              <CampoInput
                id="icmsStAliquota"
                label="Alíquota ICMS-ST (%)"
                v-model="item.icmsStAliquota"
                type="moeda"
                :casas-decimais="4"
                placeholder="Ex: 18%"
                :disabled="!podeEditar"
                dica="Alíquota do ICMS-ST aplicada sobre a base calculada com MVA."
              />
            </div>
            <div class="col-md-4">
              <CampoInput
                id="icmsStReducaoBasePercentual"
                label="Redução Base ICMS-ST (%)"
                v-model="item.icmsStReducaoBasePercentual"
                type="moeda"
                :casas-decimais="4"
                placeholder="Ex: 0%"
                :disabled="!podeEditar"
                dica="Percentual de redução de base aplicada no cálculo do ICMS-ST."
              />
            </div>
          </div>

          <!-- Linha 2: Incentivo Estadual (master + card dependente) -->
          <div class="row g-3 mt-3">
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstTipoIncentivoIcms"
                label="Tipo de Incentivo ICMS"
                v-model="item.lstTipoIncentivoIcms"
                tabela="PerfilTributario"
                :disabled="!podeEditar"
                label-info="Define como o ICMS é tratado no cálculo (redução de base, crédito presumido ou isenção/deferimento)."
              />
            </div>
            <div class="col-md-9">
              <div
                class="p-3 rounded-3 border h-100"
                :class="temIncentivoIcms ? 'bg-light-subtle border-primary-subtle' : 'bg-body-tertiary border-secondary-subtle opacity-50'"
              >
                <div class="d-flex align-items-center mb-2">
                  <i class="bi bi-piggy-bank me-2" :class="temIncentivoIcms ? 'text-primary' : 'text-secondary'"></i>
                  <span class="small fw-semibold" :class="temIncentivoIcms ? 'text-primary' : 'text-secondary'">
                    Configurações do Incentivo Estadual
                  </span>
                  <span v-if="!temIncentivoIcms" class="badge bg-secondary ms-2" style="font-size: 0.65rem;">BLOQUEADO</span>
                  <span v-else class="badge bg-primary ms-2" style="font-size: 0.65rem;">ATIVO</span>
                </div>
                <div class="row g-3">
                  <div class="col-md-5" v-if="tipoIncentivo === FISC_TP_INCENTIVO.ReducaoBase">
                    <CampoInput
                      id="icmsAliquotaEfetiva"
                      label="Alíquota Efetiva (%) - COMPETE"
                      v-model="item.icmsAliquotaEfetiva"
                      type="moeda"
                      :casas-decimais="4"
                      placeholder="Ex: 5%"
                      required
                      :disabled="!podeEditar || !temIncentivoIcms"
                      dica="Alíquota efetiva de ICMS (base reduzida × cheia). É ela que compõe o preço."
                    >
                      <template #label-extra>
                        <span v-if="campoIcmsFator === 'icmsAliquotaEfetiva'" class="d-inline-flex align-items-center gap-1" @click.stop>
                          <input
                            id="chkFatorIcmsEfetiva"
                            class="form-check-input m-0"
                            type="checkbox"
                            v-model="fatorComponentes.icmsAliquota"
                            :disabled="!podeEditar"
                          >
                          <label class="text-body-secondary fst-italic mb-0" for="chkFatorIcmsEfetiva" style="font-size: 0.72rem; cursor: pointer;" title="O ICMS entra no fator usando a alíquota efetiva.">Compõe Fator</label>
                        </span>
                      </template>
                    </CampoInput>
                  </div>
                  <div class="col-md-5" v-if="tipoIncentivo === FISC_TP_INCENTIVO.CreditoPresumido">
                    <CampoInput
                      id="percentualCreditoPresumidoIcms"
                      label="% Crédito Presumido"
                      v-model="item.percentualCreditoPresumidoIcms"
                      type="moeda"
                      :casas-decimais="4"
                      placeholder="Ex: 10%"
                      required
                      :disabled="!podeEditar || !temIncentivoIcms"
                      dica="Percentual de crédito presumido de ICMS sobre a base. O ICMS destacado permanece a alíquota cheia."
                    />
                  </div>
                  <div class="col-md-5 d-flex align-items-center">
                    <div class="form-text small text-body-secondary mb-0">
                      <i class="bi bi-info-circle me-1"></i>
                      Ganho do incentivo: <strong>{{ ganhoIncentivoPercentual }}%</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quadro: Contribuições Federais (PIS/COFINS) -->
      <div class="card mb-4 shadow-sm border-0">
        <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
          <h6 class="mb-0 text-body-emphasis"><i class="bi bi-bank me-2"></i>Contribuições Sociais (PIS / COFINS)</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstCstPis"
                label="CST PIS"
                v-model="item.lstCstPis"
                tabela="PerfilTributario"
                :required="isModoXerife"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="pisAliquota"
                label="Alíquota PIS (%)"
                v-model="item.pisAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              >
                <template #label-extra>
                  <span class="d-inline-flex align-items-center gap-1" @click.stop>
                    <input
                      id="chkFatorPis"
                      class="form-check-input m-0"
                      type="checkbox"
                      v-model="fatorComponentes.pisAliquota"
                      :disabled="!podeEditar"
                    >
                    <label class="text-body-secondary fst-italic mb-0" for="chkFatorPis" style="font-size: 0.72rem; cursor: pointer;">Compõe Fator</label>
                  </span>
                </template>
              </CampoInput>
            </div>
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstCstCofins"
                label="CST COFINS"
                v-model="item.lstCstCofins"
                tabela="PerfilTributario"
                :required="isModoXerife"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="cofinsAliquota"
                label="Alíq. COFINS (%)"
                v-model="item.cofinsAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              >
                <template #label-extra>
                  <span class="d-inline-flex align-items-center gap-1" @click.stop>
                    <input
                      id="chkFatorCofins"
                      class="form-check-input m-0"
                      type="checkbox"
                      v-model="fatorComponentes.cofinsAliquota"
                      :disabled="!podeEditar"
                    >
                    <label class="text-body-secondary fst-italic mb-0" for="chkFatorCofins" style="font-size: 0.72rem; cursor: pointer;">Compõe Fator</label>
                  </span>
                </template>
              </CampoInput>
            </div>
          </div>
        </div>
      </div>

      <!-- Quadro: Impostos Federais (IPI, II, IE) -->
      <div class="card mb-4 shadow-sm border-0">
        <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
          <h6 class="mb-0 text-body-emphasis"><i class="bi bi-globe-americas me-2"></i>Impostos Federais & Comex (IPI / II / IE)</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstCstIpi"
                label="CST IPI"
                v-model="item.lstCstIpi"
                tabela="PerfilTributario"
                :required="isModoXerife"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="ipiAliquota"
                label="Alíquota IPI (%)"
                v-model="item.ipiAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="iiAliquota"
                label="Imposto Importação (%)"
                v-model="item.iiAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="ieAliquota"
                label="Imposto Exportação (%)"
                v-model="item.ieAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- ============================================================ -->
    <!-- BLOCO: SERVIÇO                                                -->
    <!-- ============================================================ -->
    <template v-if="naturezaAtendido === PROD_NATUREZA.Servico || !naturezaAtendido">
      <!-- Quadro: Impostos Municipais (ISS) -->
      <div class="card mb-4 shadow-sm border-0">
        <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
          <h6 class="mb-0 text-body-emphasis"><i class="bi bi-building me-2"></i>Impostos Municipais (ISS)</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoInput
                id="issAliquota"
                label="Alíquota ISS (%)"
                v-model="item.issAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoSelect
                id="reterIssFonte"
                label="Reter ISS na Fonte?"
                v-model="item.reterIssFonte"
                :options="simNaoOptions"
                option-value="id"
                option-label="label"
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Quadro: Retenções Federais na Fonte -->
      <div class="card shadow-sm border-0">
        <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
          <h6 class="mb-0 text-body-emphasis"><i class="bi bi-funnel me-2"></i>Retenções Federais na Fonte (Serviços Tomados)</h6>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-2">
              <CampoInput
                id="irrfRetidoAliquota"
                label="Alíq. IRRF Retido (%)"
                v-model="item.irrfRetidoAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="inssRetidoAliquota"
                label="Alíq. INSS Retido (%)"
                v-model="item.inssRetidoAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="csllRetidaAliquota"
                label="Alíq. CSLL Retida (%)"
                v-model="item.csllRetidaAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoInput
                id="pisRetidoAliquota"
                label="Alíq. PIS Retido (%)"
                v-model="item.pisRetidoAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="cofinsRetidaAliquota"
                label="Alíq. COFINS Retida (%)"
                v-model="item.cofinsRetidaAliquota"
                type="moeda"
                :casas-decimais="4"
                :disabled="!podeEditar"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ============================================================ -->
    <!-- BLOCO: REFORMA TRIBUTÁRIA (comum a Produto Físico e Serviço)   -->
    <!-- CBS e IBS incidem sobre mercadorias E serviços                 -->
    <!-- ============================================================ -->
    <div class="card mb-4 shadow-sm border-0">
      <div class="card-header bg-warning bg-opacity-10 border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-warning-emphasis"><i class="bi bi-graph-up-arrow me-2"></i>Reforma Tributária (CBS / IBS / IS)</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="cbsAliquota"
              label="CBS (%) — Contribuição sobre Bens e Serviços"
              v-model="item.cbsAliquota"
              type="moeda"
              :casas-decimais="4"
              placeholder="IVA Federal"
              :disabled="!podeEditar"
              dica="Substituirá PIS e COFINS. Incide sobre mercadorias e serviços."
            >
              <template #label-extra>
                <span class="d-inline-flex align-items-center gap-1" @click.stop>
                  <input
                    id="chkFatorCbs"
                    class="form-check-input m-0"
                    type="checkbox"
                    v-model="fatorComponentes.cbsAliquota"
                    :disabled="!podeEditar"
                  >
                  <label class="text-body-secondary fst-italic mb-0" for="chkFatorCbs" style="font-size: 0.72rem; cursor: pointer;">Compõe Fator</label>
                </span>
              </template>
            </CampoInput>
          </div>
          <div class="col-md-4">
            <CampoInput
              id="ibsAliquota"
              label="IBS (%) — Imposto sobre Bens e Serviços"
              v-model="item.ibsAliquota"
              type="moeda"
              :casas-decimais="4"
              placeholder="IVA Estadual/Municipal"
              :disabled="!podeEditar"
              dica="Substituirá ICMS e ISS. Incide sobre mercadorias e serviços."
            >
              <template #label-extra>
                <span class="d-inline-flex align-items-center gap-1" @click.stop>
                  <input
                    id="chkFatorIbs"
                    class="form-check-input m-0"
                    type="checkbox"
                    v-model="fatorComponentes.ibsAliquota"
                    :disabled="!podeEditar"
                  >
                  <label class="text-body-secondary fst-italic mb-0" for="chkFatorIbs" style="font-size: 0.72rem; cursor: pointer;">Compõe Fator</label>
                </span>
              </template>
            </CampoInput>
          </div>
          <div class="col-md-4">
            <CampoInput
              id="isAliquota"
              label="IS (%) — Imposto Seletivo"
              v-model="item.isAliquota"
              type="moeda"
              :casas-decimais="4"
              placeholder="Imposto do Pecado"
              :disabled="!podeEditar"
              dica="Incide sobre produtos específicos (cigarros, bebidas, defensivos agrícolas)."
            />
          </div>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <!-- GUARDIÃO: Propriedade contexto="tenant" injetada para correção da busca -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    @fechar="mostrarAuditoria = false"
    tabela="PerfilTributario"
    contexto="tenant"
    :chave-primaria="item.idPerfilTributario"
    :titulo-registro="item.descricao"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { PROD_NATUREZA } from '@/constants/PROD_NATUREZA'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { FISC_TP_INCENTIVO } from '@/constants/FISC_TP_INCENTIVO'
import { agoraUtcISO } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// Controlo de Acesso e Planos (Tenant)
const podeEditar = computed(() => authStore.pode('PERFIL_TRIBUTARIO_COMPLETO'))

// Lógica de Personificação e Crachá Dinâmico
const nivelFiscal = ref('CARREGANDO')
const nivelFiscalDescricao = ref('A Carregar...')

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

// Listas Auxiliares (Soft Delete)
const listas = reactive({})

// GUARDIÃO: Detecta dinamicamente se a lista de Status usa idListaSistemaItem ou
// idListaClienteItem (o metadata mapeia LstStatus para ListasCliente, que retorna
// itens com idListaClienteItem; diferente do ListasSistema que usa idListaSistemaItem).
// Mesma estratégia usada em LoteFabricacaoCadastro.vue:90-96.
const optionValueStatusField = computed(() => {
  const items = listas.LstStatus || []
  if (items.length === 0) return 'idListaSistemaItem'
  if (items[0].idListaSistemaItem !== undefined) return 'idListaSistemaItem'
  if (items[0].idListaClienteItem !== undefined) return 'idListaClienteItem'
  return 'id'
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['PerfilTributario'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas do PerfilTributario:', e)
  }
}

const carregarNivelFiscal = async () => {
  let nivelCodigo = NV_MODULO_FISCAL.Nenhum
  nivelFiscalDescricao.value = 'A Carregar...'

  try {
    if (authStore.isAdminInterno) {
      if (authStore.empresaAtiva) {
        const resEmpresa = await api.get(`/Empresas/${authStore.empresaAtiva}`)
        const idPlano = resEmpresa.data.idPlanoVigente || resEmpresa.data.IdPlanoVigente

        if (idPlano) {
          const resPlano = await api.get(`/Planos/${idPlano}`)
          nivelCodigo = resPlano.data.lstNivelModuloFiscal || resPlano.data.LstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
        }
      }
    } else {
      nivelCodigo = authStore.lstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
    }
  } catch (e) {
    // Silenciado
  }

  nivelFiscal.value = String(nivelCodigo).toUpperCase()

  try {
    if (nivelCodigo !== NV_MODULO_FISCAL.Nenhum) {
      const resLista = await api.get('/ListasSistema/lookup/NV_MODULO_FISCAL')
      const itemLista = resLista.data.find(i =>
        String(i.idListaSistemaItem).toUpperCase() === nivelFiscal.value ||
        String(i.textoLista).toUpperCase() === nivelFiscal.value
      )
      nivelFiscalDescricao.value = itemLista ? itemLista.textoLista : nivelFiscal.value
    } else {
      nivelFiscalDescricao.value = 'Nenhum'
    }
  } catch (e) {
    nivelFiscalDescricao.value = nivelFiscal.value
  }
}

// Regra Xerife baseada na avaliação inteligente
const isModoXerife = computed(() => {
  const code = nivelFiscal.value.toUpperCase()
  const desc = nivelFiscalDescricao.value.toUpperCase()

  if (code === NV_MODULO_FISCAL.Nenhum || desc === 'A CARREGAR...') return false
  return code.includes(NV_MODULO_FISCAL.Compliance) || desc.includes(NV_MODULO_FISCAL.Compliance)
})

// GUARDIÃO: Options para o campo Natureza do Item Atendido
// Usa as constantes PROD_NATUREZA para exibir labels amigáveis
const naturezaOptions = [
  { id: PROD_NATUREZA.ProdutoFisico, label: 'Produto Físico' },
  { id: PROD_NATUREZA.Servico, label: 'Serviço' }
]

// Computed que rastreia a natureza selecionada para controle de visibilidade dos blocos
const naturezaAtendido = computed(() => {
  return item.value?.lstNaturezaItemAtendido || ''
})

// O Tipo de Incentivo ICMS é carregado automaticamente do Dicionário de Metadados
// (CampoBuscaLista com tabela="PerfilTributario").

// GUARDIÃO: Chaves alinhadas perfeitamente com o DTO do C#.
// O Chefe ordenou: Não iniciar com zero para forçar a validação de required na tela.
const formDefaults = {
  idPerfilTributario: null,
  descricao: '',
  lstStatus: '',
  lstNaturezaItemAtendido: '',
  lstCstIcms: '',
  lstCsosn: '',
  icmsAliquota: null,
  icmsAliquotaEfetiva: null,
  icmsStMvaPercentual: null,
  icmsStAliquota: null,
  icmsStReducaoBasePercentual: null,
  lstTipoIncentivoIcms: FISC_TP_INCENTIVO.Nenhum,
  percentualCreditoPresumidoIcms: null,
  lstCstPis: '',
  pisAliquota: null,
  lstCstCofins: '',
  cofinsAliquota: null,
  lstCstIpi: '',
  ipiAliquota: null,
  iiAliquota: null,
  ieAliquota: null,
  issAliquota: null,
  reterIssFonte: false,
  irrfRetidoAliquota: null,
  inssRetidoAliquota: null,
  csllRetidaAliquota: null,
  pisRetidoAliquota: null,
  cofinsRetidaAliquota: null,
  difalAliquota: null,
  cbsAliquota: null,
  ibsAliquota: null,
  isAliquota: null,
  fatorImpostos: 1,
  fatorImpostosMemoriaJson: null,
  dataInicioVigencia: null,
  dataFimVigencia: null
}

const { item, isLoading, isEditing, entityInfo, submitForm, initializeForm, handleCancel } = useFormulario(props, emit, formDefaults)

const mostrarAuditoria = ref(false)

// GUARDIÃO: Interceptor de tipagem para proteger o C# de quebras de formato (vírgulas e strings)
const sanitizarDecimal = (valor) => {
  if (valor === null || valor === undefined || valor === '') return 0
  if (typeof valor === 'string') {
    const formatado = valor.replace(/\./g, '').replace(',', '.')
    const floatVal = parseFloat(formatado)
    return isNaN(floatVal) ? 0 : floatVal
  }
  return Number(valor) || 0
}

// ============================================================
// INCENTIVOS ESTADUAIS DE ICMS — resolução para o fator
// ============================================================
// Tipo de incentivo corrente (default: Nenhum)
const tipoIncentivo = computed(() => item.value?.lstTipoIncentivoIcms || FISC_TP_INCENTIVO.Nenhum)

// Há incentivo ativo? (controla o estado ATIVO/BLOQUEADO do card dependente)
const temIncentivoIcms = computed(() => tipoIncentivo.value !== FISC_TP_INCENTIVO.Nenhum)

// ICMS resolvido que compõe o fator (por dentro), conforme o tipo de incentivo
const icmsResolvidoParaFator = computed(() => {
  const cheia = sanitizarDecimal(item.value?.icmsAliquota)
  const efetiva = sanitizarDecimal(item.value?.icmsAliquotaEfetiva)
  switch (tipoIncentivo.value) {
    case FISC_TP_INCENTIVO.ReducaoBase:
      return efetiva > 0 ? efetiva : cheia
    case FISC_TP_INCENTIVO.CreditoPresumido:
      return cheia
    case FISC_TP_INCENTIVO.Isencaodeferimento:
      return 0
    default:
      return cheia
  }
})

// Campo EXATO que compõe o fator — usado na memória de cálculo e para posicionar a flag "Compõe Fator".
// Nenhum/Crédito Presumido → icmsAliquota (cheia); Redução Base → icmsAliquotaEfetiva; Isenção → null (não compõe).
const campoIcmsFator = computed(() => {
  switch (tipoIncentivo.value) {
    case FISC_TP_INCENTIVO.ReducaoBase:
      return 'icmsAliquotaEfetiva'
    case FISC_TP_INCENTIVO.Isencaodeferimento:
      return null
    default:
      return 'icmsAliquota'
  }
})

// Percentual de ganho do incentivo sobre a base (preview)
const ganhoIncentivoPercentual = computed(() => {
  const cheia = sanitizarDecimal(item.value?.icmsAliquota)
  const efetiva = sanitizarDecimal(item.value?.icmsAliquotaEfetiva)
  const credito = sanitizarDecimal(item.value?.percentualCreditoPresumidoIcms)
  switch (tipoIncentivo.value) {
    case FISC_TP_INCENTIVO.ReducaoBase:
      return Number((cheia - (efetiva > 0 ? efetiva : cheia)).toFixed(4))
    case FISC_TP_INCENTIVO.CreditoPresumido:
      return Number(credito.toFixed(4))
    case FISC_TP_INCENTIVO.Isencaodeferimento:
      return Number(cheia.toFixed(4))
    default:
      return 0
  }
})

// ============================================================
// FATOR DE IMPOSTOS — Memória de cálculo (gross-up)
// ============================================================
// Somente tributos EMBUTIDOS no preço compõem o fator.
// Tributos "por fora" (IPI, II, IE, ISS, IS e retenções) NÃO entram.
const CAMPOS_FATOR = [
  { campo: 'icmsAliquota', label: 'ICMS' },
  { campo: 'pisAliquota', label: 'PIS' },
  { campo: 'cofinsAliquota', label: 'COFINS' },
  { campo: 'cbsAliquota', label: 'CBS' },
  { campo: 'ibsAliquota', label: 'IBS' }
]

const fatorComponentes = reactive(
  Object.fromEntries(CAMPOS_FATOR.map(c => [c.campo, false]))
)

// Para o ICMS, usa a alíquota resolvida pelo tipo de incentivo (cheia/efetiva/0).
const aliquotaDe = (campo) => {
  if (campo === 'icmsAliquota') return icmsResolvidoParaFator.value
  return sanitizarDecimal(item.value?.[campo])
}

const componentesAtivos = computed(() =>
  CAMPOS_FATOR.filter(c => fatorComponentes[c.campo] && aliquotaDe(c.campo) > 0)
)

const somaAliquotas = computed(() =>
  componentesAtivos.value.reduce((s, c) => s + aliquotaDe(c.campo), 0)
)

const fatorCalculado = computed(() => {
  const f = 1 - somaAliquotas.value / 100
  return Number((f > 0 ? f : 0).toFixed(4))
})

const fatorAutomatico = computed(() =>
  Object.values(fatorComponentes).some(Boolean)
)

// Sincroniza o fator com os componentes marcados (modo automático)
watch([fatorAutomatico, somaAliquotas], () => {
  if (fatorAutomatico.value) {
    item.value.fatorImpostos = fatorCalculado.value
  }
}, { immediate: true })

// Restaura os checkboxes "Compõe Fator" a partir da memória persistida
watch(() => item.value?.fatorImpostosMemoriaJson, (json) => {
  if (!json) {
    CAMPOS_FATOR.forEach(c => { fatorComponentes[c.campo] = false })
    return
  }
  try {
    const memoria = JSON.parse(json)
    const mapa = Object.fromEntries((memoria.componentes || []).map(c => [c.campo, !!c.inclui]))
    CAMPOS_FATOR.forEach(c => {
      if (c.campo === 'icmsAliquota') {
        // O ICMS pode ter sido gravado como icmsAliquota (cheia) ou icmsAliquotaEfetiva (redução base)
        fatorComponentes.icmsAliquota = !!(mapa.icmsAliquota || mapa.icmsAliquotaEfetiva)
      } else {
        fatorComponentes[c.campo] = !!mapa[c.campo]
      }
    })
  } catch (e) {
    CAMPOS_FATOR.forEach(c => { fatorComponentes[c.campo] = false })
  }
}, { immediate: true })

const formatarNumeroFator = (valor) =>
  Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

// Linhas da memória de cálculo do fator — usadas no JSON persistido E no tooltip do selo "Calculado"
const memoriaDeCalculo = computed(() => {
  const soma = Number(somaAliquotas.value.toFixed(4))
  const fator = fatorCalculado.value
  const fatorSeguro = fator > 0 ? fator : 1
  const inclusos = componentesAtivos.value
  const expressao = inclusos.length > 0
    ? inclusos.map(c => `${c.label} ${formatarNumeroFator(aliquotaDe(c.campo))}%`).join(' + ')
    : 'nenhum tributo embutido'

  return [
    `Fator = 1 - (${expressao}) / 100`,
    `Soma das alíquotas = ${formatarNumeroFator(soma)}%`,
    `Fator = 1 - ${formatarNumeroFator(soma / 100)} = ${formatarNumeroFator(fator)}`,
    `Gross-up: Preço de Venda = Custo / ${formatarNumeroFator(fator)} (ex.: R$ 1.000,00 => R$ ${formatarNumeroFator(1000 / fatorSeguro)})`
  ]
})

// Tooltip estruturado do selo "Calculado" — uma informação por linha
const memoriaCalculoTooltip = computed(() => memoriaDeCalculo.value.join('\n'))

// Monta o JSON de memória de cálculo do fator (gross-up)
function buildMemoriaFator () {
  const componentes = CAMPOS_FATOR.map(c => {
    const isIcms = c.campo === 'icmsAliquota'
    const comp = {
      campo: isIcms ? (campoIcmsFator.value || 'icmsAliquota') : c.campo,
      label: c.label,
      tipo: 'Embutido',
      inclui: isIcms ? (fatorComponentes[c.campo] && campoIcmsFator.value !== null) : fatorComponentes[c.campo],
      aliquota: aliquotaDe(c.campo)
    }
    if (isIcms) comp.resolvidoPorIncentivo = true
    return comp
  })

  const soma = Number(somaAliquotas.value.toFixed(4))
  const fator = fatorCalculado.value

  return JSON.stringify({
    versao: '1.0',
    modo: fatorAutomatico.value ? 'Automatico' : 'Manual',
    bloqueadoParaEdicaoManual: fatorAutomatico.value,
    tipoIncentivoIcms: tipoIncentivo.value,
    formula: 'Fator = 1 - (soma das alíquotas dos tributos embutidos) / 100',
    fundamento: "Cálculo por dentro (gross-up). Só tributos embutidos compõem o fator: ICMS (cheia/efetiva/0 conforme o incentivo), PIS, COFINS, CBS, IBS. Tributos 'por fora' (IPI, II, IE, ISS, IS e retenções) NÃO entram.",
    componentes,
    somaAliquotas: soma,
    fatorCalculado: fator,
    memoriaDeCalculo: memoriaDeCalculo.value,
    calculadoEm: agoraUtcISO(),
    origem: 'sistema'
  })
}

async function salvar () {
  if (!podeEditar.value) return

  // GUARDIÃO: Construção blindada do Payload
  const payload = {
    idPerfilTributario: item.value.idPerfilTributario,
    // GUARDIÃO: Escopo multiorganização (Tenant) — injeta a organização ativa.
    // O backend faz override absoluto via ITenantContext; o envio aqui é defesa em profundidade.
    idOrganizacao: authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null,
    descricao: item.value.descricao,
    lstStatus: item.value.lstStatus || null,
    lstCstIcms: item.value.lstCstIcms || null,
    lstCsosn: item.value.lstCsosn || null,
    lstCstPis: item.value.lstCstPis || null,
    lstCstCofins: item.value.lstCstCofins || null,
    lstCstIpi: item.value.lstCstIpi || null,
    lstNaturezaItemAtendido: item.value.lstNaturezaItemAtendido || null,
    lstTipoIncentivoIcms: item.value.lstTipoIncentivoIcms || null,
    icmsAliquota: sanitizarDecimal(item.value.icmsAliquota),
    icmsAliquotaEfetiva: tipoIncentivo.value === FISC_TP_INCENTIVO.ReducaoBase
      ? sanitizarDecimal(item.value.icmsAliquotaEfetiva)
      : 0,
    percentualCreditoPresumidoIcms: tipoIncentivo.value === FISC_TP_INCENTIVO.CreditoPresumido
      ? sanitizarDecimal(item.value.percentualCreditoPresumidoIcms)
      : 0,
    icmsStMvaPercentual: sanitizarDecimal(item.value.icmsStMvaPercentual),
    icmsStAliquota: sanitizarDecimal(item.value.icmsStAliquota),
    icmsStReducaoBasePercentual: sanitizarDecimal(item.value.icmsStReducaoBasePercentual),
    pisAliquota: sanitizarDecimal(item.value.pisAliquota),
    cofinsAliquota: sanitizarDecimal(item.value.cofinsAliquota),
    ipiAliquota: sanitizarDecimal(item.value.ipiAliquota),
    iiAliquota: sanitizarDecimal(item.value.iiAliquota),
    ieAliquota: sanitizarDecimal(item.value.ieAliquota),

    // Novas retenções e ISS
    issAliquota: sanitizarDecimal(item.value.issAliquota),
    reterIssFonte: item.value.reterIssFonte === true || String(item.value.reterIssFonte) === 'true',
    irrfRetidoAliquota: sanitizarDecimal(item.value.irrfRetidoAliquota),
    inssRetidoAliquota: sanitizarDecimal(item.value.inssRetidoAliquota),
    csllRetidaAliquota: sanitizarDecimal(item.value.csllRetidaAliquota),
    pisRetidoAliquota: sanitizarDecimal(item.value.pisRetidoAliquota),
    cofinsRetidaAliquota: sanitizarDecimal(item.value.cofinsRetidaAliquota),

    // DIFAL e Reforma Tributária
    difalAliquota: sanitizarDecimal(item.value.difalAliquota),
    cbsAliquota: sanitizarDecimal(item.value.cbsAliquota),
    ibsAliquota: sanitizarDecimal(item.value.ibsAliquota),
    isAliquota: sanitizarDecimal(item.value.isAliquota),

    // Engenharia Financeira
    fatorImpostos: sanitizarDecimal(item.value.fatorImpostos),
    fatorImpostosMemoriaJson: fatorAutomatico.value ? buildMemoriaFator() : null,

    // Vigência Temporal
    dataInicioVigencia: item.value.dataInicioVigencia || null,
    dataFimVigencia: item.value.dataFimVigencia || null
  }

  await submitForm('PerfisTributarios', 'idPerfilTributario', payload)
}

async function salvarAlteracoes () {
  if (!podeEditar.value) return

  const payload = {
    idPerfilTributario: item.value.idPerfilTributario,
    // GUARDIÃO: Escopo multiorganização (Tenant) — injeta a organização ativa.
    // O backend faz override absoluto via ITenantContext; o envio aqui é defesa em profundidade.
    idOrganizacao: authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null,
    descricao: item.value.descricao,
    lstStatus: item.value.lstStatus || null,
    lstCstIcms: item.value.lstCstIcms || null,
    lstCsosn: item.value.lstCsosn || null,
    lstCstPis: item.value.lstCstPis || null,
    lstCstCofins: item.value.lstCstCofins || null,
    lstCstIpi: item.value.lstCstIpi || null,
    lstNaturezaItemAtendido: item.value.lstNaturezaItemAtendido || null,
    lstTipoIncentivoIcms: item.value.lstTipoIncentivoIcms || null,
    icmsAliquota: sanitizarDecimal(item.value.icmsAliquota),
    icmsAliquotaEfetiva: tipoIncentivo.value === FISC_TP_INCENTIVO.ReducaoBase
      ? sanitizarDecimal(item.value.icmsAliquotaEfetiva)
      : 0,
    percentualCreditoPresumidoIcms: tipoIncentivo.value === FISC_TP_INCENTIVO.CreditoPresumido
      ? sanitizarDecimal(item.value.percentualCreditoPresumidoIcms)
      : 0,
    icmsStMvaPercentual: sanitizarDecimal(item.value.icmsStMvaPercentual),
    icmsStAliquota: sanitizarDecimal(item.value.icmsStAliquota),
    icmsStReducaoBasePercentual: sanitizarDecimal(item.value.icmsStReducaoBasePercentual),
    pisAliquota: sanitizarDecimal(item.value.pisAliquota),
    cofinsAliquota: sanitizarDecimal(item.value.cofinsAliquota),
    ipiAliquota: sanitizarDecimal(item.value.ipiAliquota),
    iiAliquota: sanitizarDecimal(item.value.iiAliquota),
    ieAliquota: sanitizarDecimal(item.value.ieAliquota),
    issAliquota: sanitizarDecimal(item.value.issAliquota),
    reterIssFonte: item.value.reterIssFonte === true || String(item.value.reterIssFonte) === 'true',
    irrfRetidoAliquota: sanitizarDecimal(item.value.irrfRetidoAliquota),
    inssRetidoAliquota: sanitizarDecimal(item.value.inssRetidoAliquota),
    csllRetidaAliquota: sanitizarDecimal(item.value.csllRetidaAliquota),
    pisRetidoAliquota: sanitizarDecimal(item.value.pisRetidoAliquota),
    cofinsRetidaAliquota: sanitizarDecimal(item.value.cofinsRetidaAliquota),
    difalAliquota: sanitizarDecimal(item.value.difalAliquota),
    cbsAliquota: sanitizarDecimal(item.value.cbsAliquota),
    ibsAliquota: sanitizarDecimal(item.value.ibsAliquota),
    isAliquota: sanitizarDecimal(item.value.isAliquota),
    fatorImpostos: sanitizarDecimal(item.value.fatorImpostos),
    fatorImpostosMemoriaJson: fatorAutomatico.value ? buildMemoriaFator() : null,
    dataInicioVigencia: item.value.dataInicioVigencia || null,
    dataFimVigencia: item.value.dataFimVigencia || null
  }

  try {
    isLoading.value = true
    const res = item.value.idPerfilTributario
      ? await api.put(`/PerfisTributarios/${item.value.idPerfilTributario}`, payload)
      : await api.post('/PerfisTributarios', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

watch(() => authStore.empresaAtiva, () => {
  carregarNivelFiscal()
})

// GUARDIÃO: Smart Clean ao trocar a natureza do item.
// Quando o usuário alterna entre ProdutoFísico e Serviço, os campos
// do bloco oposto são zerados/limpos para evitar dados órfãos no payload.
watch(() => item.value?.lstNaturezaItemAtendido, (novoValor, valorAntigo) => {
  if (!novoValor || !valorAntigo) return
  if (novoValor === valorAntigo) return

  const isProduto = novoValor === PROD_NATUREZA.ProdutoFisico
  const isServico = novoValor === PROD_NATUREZA.Servico

  if (isServico) {
    // Limpa campos de Produto Físico (ICMS, PIS/COFINS, IPI/II/IE).
    // CBS/IBS/IS NÃO são zerados: incidem também sobre serviços (Reforma Tributária).
    item.value.lstCstIcms = ''
    item.value.lstCsosn = ''
    item.value.icmsAliquota = null
    item.value.icmsAliquotaEfetiva = null
    item.value.lstCstPis = ''
    item.value.pisAliquota = null
    item.value.lstCstCofins = ''
    item.value.cofinsAliquota = null
    item.value.lstCstIpi = ''
    item.value.ipiAliquota = null
    item.value.iiAliquota = null
    item.value.ieAliquota = null
    item.value.difalAliquota = null
    item.value.lstTipoIncentivoIcms = FISC_TP_INCENTIVO.Nenhum
    item.value.percentualCreditoPresumidoIcms = null

    // Desmarca os tributos de Produto Físico que compõem o fator (CBS/IBS permanecem)
    fatorComponentes.icmsAliquota = false
    fatorComponentes.pisAliquota = false
    fatorComponentes.cofinsAliquota = false
  }

  if (isProduto) {
    // Limpa campos de Serviço (ISS e Retenções)
    item.value.issAliquota = null
    item.value.reterIssFonte = false
    item.value.irrfRetidoAliquota = null
    item.value.inssRetidoAliquota = null
    item.value.csllRetidaAliquota = null
    item.value.pisRetidoAliquota = null
    item.value.cofinsRetidaAliquota = null
  }
})

// GUARDIÃO: A correlação do incentivo ICMS é aplicada no PAYLOAD (type-aware),
// sem mutar o modelo — evita apagar dados legados no carregamento e preserva
// o valor digitado caso o usuário volte ao tipo anterior.

onMounted(() => {
  carregarNivelFiscal()
  carregarListas()
})
</script>
